import type { ProductListResponse } from './types/productList';
import type { OrchWorkspacesResponse } from './types/orchWorkspaces';
import type { OrchRuntimeDetailsResponse } from './types/orchRuntimeDetails';
import type { DeleteAppCIJobResponse } from './types/appciDeleteJob';
import type { DeleteRuntimeJobResponse } from './types/orchRuntimeDelete';

export function createDummyProducts(): ProductListResponse {
  return {
    products: [
      {
        product_id: 'dummy-1',
        product_name: 'DummyProduct1',
        provisioning_method: 'self_service',
        team: 'Sample Team',
        budget: 'Sample Budget',
        purpose: 'Sample Purpose',
        description: 'This is a dummy product for testing',
        jira_url: 'https://jira.example.com/browse/DUMMY',
        confluence_url: 'https://confluence.example.com/pages/dummy',
        teams_url: 'https://teams.microsoft.com/l/channel/dummy',
        github_url: 'https://github.com/example/dummy-product',
        phase_id: null,
        latest_release_date: null,
        latest_release_version: '1.0.0',
        next_release_date: null,
        next_release_version: '1.1.0',
        create_date: '2026-01-01',
        update_date: '2026-05-28',
      },
      {
        product_id: 'dummy-jsm',
        product_name: 'DummyJsmProduct',
        provisioning_method: 'jsm',
        team: 'Sample Team',
        budget: 'Sample Budget',
        purpose: 'Sample Purpose',
        description: 'This is a dummy JSM product for testing',
        jira_url: 'https://jira.example.com/browse/DUMMY-JSM',
        confluence_url: '',
        teams_url: '',
        github_url: '',
        phase_id: null,
        latest_release_date: null,
        latest_release_version: '',
        next_release_date: null,
        next_release_version: '',
        create_date: '2026-01-01',
        update_date: '2026-05-28',
      },
    ],
  };
}

export function createDummyOrchWorkspaces(
  productId: string,
): OrchWorkspacesResponse {
  const dummyResponse: OrchWorkspacesResponse = {
    product: {
      product_id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
      product_name: 'my-product',
      provisioning_method: 'self_service',
      external_product_id: 'ext-my-product',
      team: 'my-team',
      budget: 'AB123',
      provision_status: 'fully_provisioned',
      // provision_status: 'workspace_provisioned',
    },
    workspaces: [
      {
        workspace: {
          id: 'f1e2d3c4-b5a6-7890-abcd-ef1234567890',
          name: 'my-workspace',
          description: 'my workspace description',
        },
        entra_group: {
          id: 'c1b2a3d4-e5f6-7890-abcd-ef1234567890',
          display_name: 'my-group',
        },
        app_cis: [
          {
            id: 'b1a2c3d4-e5f6-7890-abcd-ef1234567890',
            parent_workspace_id: 'f1e2d3c4-b5a6-7890-abcd-ef1234567890',
            path: 'org/repo',
          },
          // {
          //   id: 'b2a2c3d4-e5f6-7890-abcd-ef1234567890',
          //   parent_workspace_id: 'f1e2d3c4-b5a6-7890-abcd-ef1234567891',
          //   path: 'org/repo2',
          // },
        ],
        runtimeenvs: [
          {
            id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
            env: 'dev',
            name: 'my-runtimeenv',
            parent_workspace_id: 'f1e2d3c4-b5a6-7890-abcd-ef1234567890',
          },
        ],
        azure_links: [
          {
            subscription_id: 'd1c2b3a4-e5f6-7890-abcd-ef1234567890',
            tenant_id: 'e1d2c3b4-a5f6-7890-abcd-ef1234567890',
            resource_provider: 'Microsoft.Web',
            resource_name: 'my-resource',
            display_name: 'My Resource',
          },
        ],
        in_progress: {
          runtimes: [
            {
              job_id: 'dummy-update-runtimeenv-job',
              id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
              job_type: 'update',
              env: 'dev',
              env_name: 'my-runtimeenv',
              status: 'in_progress',
            },
          ],
          app_cis: [],
        },
        deleting: {},
      },
    ],
  };
  if (productId === 'dummy-jsm') {
    dummyResponse.product = {
      ...dummyResponse.product,
      product_id: 'dummy-jsm',
      product_name: 'DummyJsmProduct',
      provisioning_method: 'jsm',
      external_product_id: 'ext-dummy-jsm',
      team: 'Sample Team',
      budget: 'Sample Budget',
      provision_status: 'not_provisioned',
    };
  }
  return dummyResponse;
}

export function createDummyOrchRuntimeDetails(): OrchRuntimeDetailsResponse {
  return {
    runtime_name: 'Development Runtime',
    saas_service_domain: 'dev.example.com',
    resource_group_name: 'dev-rg-001',
    created_at: '2026-01-15T10:30:00Z',
    subscription_id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    tenant_id: 'b1c2d3e4-f5a6-7890-abcd-ef1234567890',
    service_account: 'dev-sa@example.iam.gserviceaccount.com',
    runtime_cicd_paths: [
      'dev-runtime/cicd-pipeline-1',
      'dev-runtime/cicd-pipeline-2',
    ],
    argocd_watch_targets: [
      {
        repository_name: 'dev-argocd-repo',
        repo_url: 'https://github.com/example/dev-argocd-repo.git',
        target_revision: 'refs/heads/dev-environment',
        manifest_path: 'dev/runtime-config',
      },
    ],
    argocd_application_url:
      'https://argocd.example.com/applications/dev-runtime',
    azure_kubernetes_service_name: 'dev-aks-cluster-001',
    namespace: 'dev-namespace',
  };
}

export function createDummyDeleteAppCIJob(): DeleteAppCIJobResponse {
  return {
    job_id: globalThis.crypto?.randomUUID?.() ?? 'dummy-delete-appci-job',
    request_id: null,
    status: 'github_processing',
  };
}

export function createDummyDeleteRuntimeJob(): DeleteRuntimeJobResponse {
  return {
    job_id: globalThis.crypto?.randomUUID?.() ?? 'dummy-delete-runtimeenv-job',
    request_id: null,
    status: 'in_progress',
  };
}
